import { Component } from 'react';
import { redirectWindowDom } from '../variables/redirect-window-dom';
import InternalizationService from './internalization';
import { sendOpenSearchLogs } from '../utils/helper-functions/send-open-search-logs.ts';
import { CASHIER_REDIRECT_WINDOW } from '../variables/app-variables';

const { host } = window.location;
const { langBack } = new InternalizationService();

export default class RedirectWindow extends Component {
  redirectWindow = undefined;
  closeRedirectWindowIfWindowReady = false;

  constructor(props) {
    super(props);
    this.handleRedirectMessage = this.handleRedirectMessage.bind(this);
    window.addEventListener('message', this.handleRedirectMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleRedirectMessage);
  }

  handleRedirectMessage(event) {
    let data = event.data || {};

    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }

    if (data?.type === 'redirect_window_ready') {
      if (this.redirectWindow) {
        this.closeRedirectWindowIfWindowReady = true;
      }
    }
  }

  openRedirectWindow = ({
    url,
    target = '_blank',
    features = {},
    cashier_theme,
    cashier_css,
    embedded_cashier_css = '',
    embedded_cashier_css_url = '',
  }) => {
    this.redirectWindow = window.open(url, target, this.getParamsForPopup(features));

    try {
      if (this.redirectWindow && !this.redirectWindow.closed) {
        this.redirectWindow.name = CASHIER_REDIRECT_WINDOW;
      }

      if (this.redirectWindow && this.redirectWindow.closed) {
        this.redirectWindow = null;
        // Log popup blocking for mobile webviews
        sendOpenSearchLogs({
          event_group: 'cashier_redirect_window_blocked',
          error_data: {
            message: 'Popup window immediately closed - likely blocked by mobile webview',
            userAgent: navigator.userAgent,
            url,
            target,
            features,
          },
        });
      }
    } catch (e) {
      this.redirectWindow = null;
      sendOpenSearchLogs({
        event_group: 'cashier_redirect_window_blocked',
        error_data: {
          error: {
            message: 'Error accessing window.closed - likely mobile webview security restriction',
            stack: e,
          },
          userAgent: navigator.userAgent,
          url,
          target,
          features,
        },
      });
    }

    const status = this.getStatusRedirectWindow();
    if (url === '' && status === 'open' && this.redirectWindow) {
      const windowRef = this.redirectWindow;

      // Wrap in requestAnimationFrame to ensure window is ready for document.write()
      // This fixes race conditions in mobile webviews where the window isn't immediately ready
      requestAnimationFrame(() => {
        try {
          // Verify window is still valid, open, and hasn't been replaced
          if (windowRef && !windowRef.closed && windowRef === this.redirectWindow) {
            windowRef.document.write(
              redirectWindowDom(
                langBack,
                cashier_theme,
                cashier_css,
                embedded_cashier_css,
                embedded_cashier_css_url,
              ),
            );
          }
        } catch (e) {
          // Mobile webviews may throw errors when writing to document
          console.error('Failed to write to redirect window:', e);
          sendOpenSearchLogs({
            event_group: 'cashier_redirect_window_write_failed',
            error_data: {
              error: {
                message: 'Failed to write HTML to redirect window',
                stack: e,
              },
              userAgent: navigator.userAgent,
            },
          });
        }
      });
    }
    return status;
  };

  closeRedirectWindow = () => {
    if (this.getStatusRedirectWindow() === 'open') {
      this.redirectWindow.close();
      this.closeRedirectWindowIfWindowReady = false;
    }
    this.redirectWindow = undefined;
  };

  replaceWindow = (url) => {
    if (this.getStatusRedirectWindow() === 'open') {
      this.redirectWindow.location.replace(url);
    }
  };

  isWindowSameDomain = () => {
    // Check if window exists first
    if (!this.redirectWindow) {
      return false;
    }

    try {
      // Check if window is closed
      if (this.redirectWindow.closed) {
        return false;
      }

      if (this.closeRedirectWindowIfWindowReady) {
        return true;
      }

      return (
        this.redirectWindow.history.state !== null && this.redirectWindow.location.host === host
      );
    } catch (e) {
      return false;
    }
  };

  focusRedirectWindow = () => {
    if (this.getStatusRedirectWindow() === 'open') {
      this.redirectWindow.focus();
    }
  };

  getStatusRedirectWindow = () => {
    switch (true) {
      case this.redirectWindow === undefined:
        return 'not-exist';
      case this.redirectWindow === null:
        return 'blocked';
      case this.redirectWindow?.closed:
        return 'closed';
      case typeof this.redirectWindow === 'object':
        return 'open';
      default:
        return 'indefinite';
    }
  };

  postMessageToWindow = (data) => {
    if (this.isWindowSameDomain() && this.redirectWindow) {
      this.redirectWindow.postMessage(
        {
          event_type: 'message',
          ...data,
        },
        origin,
      );
    }
  };

  getParamsForPopup = (features) => {
    let {
      window_size_width: width,
      window_size_height: height,
      payment_method_type: type,
    } = features;

    if (!width) width = type === 'cc' ? 500 : 600;
    if (!height) height = type === 'cc' ? 600 : 800;

    let left = Number(window.screen.width / 2 - +width / 2);
    let top = Number(window.screen.height / 2 - +height / 2);

    return `width=${width}, height=${height}, top=${top}, left=${left}`;
  };
}
